import React, { forwardRef, lazy, Suspense } from 'react';
// Using the public types from `react-datepicker`.
// `ReactDatePickerProps` is the correct exported type.
import { ReactDatePickerProps } from 'react-datepicker';
import cl from 'classnames';
import 'react-datepicker/dist/react-datepicker.css';
import Input, { getInputClasses } from './Input';
import './DatePicker.scss';

function CustomIcon({ onClick }: { onClick?: () => void }) {
    return (
        <button className="ew-datepicker-iconCalendar" onClick={onClick}>
            <i className="fal fa-calendar" />
        </button>
    );
}

type DatePickerProps =
    Omit<ReactDatePickerProps, 'className' | 'selected'>
    & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>
    & {
    isInvalid?: boolean;
    isValid?: boolean;
    isEmpty?: boolean;
}

const ReactDatePicker = lazy(() => import('react-datepicker').then((module) => ({ default: module.default })));

// Custom input passed to `react-datepicker`. We explicitly set `type="text"`
// because `react-datepicker` controls the displayed value (e.g. "01/08/2025"),
// which does not conform to the native `input[type="date"]` expected format
// (`yyyy-MM-dd`). Using `type="text"` prevents the browser validation error
// "The specified value does not conform to the required format".
const CustomInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ type: _ignoredType, ...inputProps }, ref) => {
    return <Input {...inputProps} ref={ref} type="text" />;
});

/**
 * DatePicker component for selecting dates with a calendar interface
 * 
 * This component wraps react-datepicker with custom styling and configuration
 * optimized for the Easy Weddings design system. It provides a user-friendly
 * calendar interface with dropdowns for month and year selection.
 * 
 * @param className - Additional CSS classes to apply to the input
 * @param placeholder - Placeholder text for the input field
 * @param rest - All other props from ReactDatePickerProps
 * 
 * @example
 * ```tsx
 * import { useState } from 'react';
 * 
 * // Basic date picker
 * const [selectedDate, setSelectedDate] = useState<Date | null>(null);
 * 
 * <Input.Date
 *   selected={selectedDate}
 *   onChange={(date) => setSelectedDate(date)}
 *   placeholder="Select a date"
 * />
 * ```
 */
const DatePicker = React.forwardRef(({
    className,
    placeholder,
    value,
    isInvalid,
    isValid,
    isEmpty,
    ...rest
}: DatePickerProps, ref: React.Ref<HTMLInputElement>) => {
    const datePickerInputClasses = cl('ew-input', className, getInputClasses({
        value,
        isInvalid,
        isValid,
        isEmpty,
    }));

    // Remove the `type` prop to avoid passing it further – we always render a text input
    // for the custom date picker control.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { type: _removedType, ...restProps } = rest as { type?: string } & typeof rest;

    return (
        <div className="ew-datepicker">
            <Suspense fallback={<Input disabled placeholder={placeholder} />}>
                <ReactDatePicker
                    customInput={<CustomInput
                        className={datePickerInputClasses}
                        // @ts-expect-error test property exists
                        data-testid={restProps['data-testid']}
                        />}
                    toggleCalendarOnIconClick
                    placeholderText={placeholder}
                    className={datePickerInputClasses}
                    icon={<CustomIcon />}
                    showMonthDropdown
                    showYearDropdown
                    showIcon
                    dateFormat="dd/MM/yyyy"
                    dropdownMode="select"
                    calendarIconClassname="ew-datepicker-iconCalendar"
                    allowSameDay
                    autoComplete="off"
                    selected={value ? new Date(value) : null}
                    {...restProps}
                />
            </Suspense>
        </div>
    );
});

export default DatePicker;
